import cn from 'classnames';
import { forwardRef } from 'react';

import { typographyVariantMapping } from './typography.utils';

import type { ReactElement } from 'react';

import type { PolymorphicRef } from '../../../utils';
import type { TypographyComponent, TypographyProps, TypographyVariantMapping } from './typography.types';

import './typography.scss';

/**
 * Typography component is used to render headings, texts, links.
 * This is a polymorphic component. The root tag can be set using `as` prop
 * It will render appropriate HTML tag for different variants.
 *
 * ### Usage
 *
 * ```tsx
 * import { Typography, Text } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <div>
 *     <Typography variant="heading-1">Banana design</Typography>
 *     <Typography variant="heading-5" color="subtl">This is a subheading for banana design</Typography>
 *     <Typography variant="text" color="body">
 *       Lorem ipsum dolor sit amet consectetur adipisicing elit
 *     </Typography>
 *   </div>
 * }
 * ```
 */
const Typography = forwardRef(
  <RootComponent extends React.ElementType, Variant extends keyof TypographyVariantMapping>(
    props: TypographyProps<RootComponent, Variant>,
    forwardedRef: PolymorphicRef<TypographyVariantMapping[Variant]>,
  ): ReactElement => {
    const {
      as,
      children,
      className,
      color,
      variant,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const Root: React.ElementType = as || typographyVariantMapping[variant || 'default'];

    const classes = cn(
      'rbui-typography',
      variant && `rbui-typography--${variant}`,
      color && `rbui-typography--color-${color}`,
      className,
    );

    return (
      <Root {...rest} className={classes} ref={forwardedRef}>
        {children}
      </Root>
    );
  },
) as TypographyComponent;

Typography.displayName = 'Typography';

export default Typography;
